/**
 * Project Experience Calculator Service
 * 
 * 案件情報から技術経験年数を自動計算するサービス
 * スキルシートの案件履歴を解析して各技術の経験年数を推定
 * 
 * @service ProjectExperienceCalculator
 */

export interface ProjectInfo {
  startDate: Date;
  endDate?: Date;
  technologies: string[];
  projectName?: string;
  description?: string;
}

export interface TechnologyExperience {
  technology: string;
  totalYears: number;
  projects: {
    projectName?: string;
    startDate: Date;
    endDate?: Date;
    durationMonths: number;
  }[];
  firstUsed: Date;
  lastUsed: Date;
  confidence: number; // 計算の信頼度 (0-1)
}

export interface ExperienceCalculationResult {
  technologies: { [technology: string]: TechnologyExperience };
  totalExperienceYears: number;
  analysisDate: Date;
  warnings: string[];
  insights: string[];
}

export class ProjectExperienceCalculator {

  /**
   * Excelの生データから案件情報を抽出して技術経験年数を計算
   */
  static async calculateFromExcelData(rawExcelData: any[][]): Promise<ExperienceCalculationResult> {
    try {
      // 案件情報を抽出
      const projects = this.extractProjectsFromExcelData(rawExcelData);

      // 技術経験年数を計算
      const result = this.calculateTechnologyExperience(projects);

      return result;
    } catch (error) {
      console.error('Excel経験年数計算エラー:', error);
      return {
        technologies: {},
        totalExperienceYears: 0,
        analysisDate: new Date(),
        warnings: [`計算中にエラーが発生しました: ${(error as Error).message}`],
        insights: []
      };
    }
  }

  /**
   * Excel生データから案件情報を抽出（改善版）
   */
  private static extractProjectsFromExcelData(rawData: any[][]): ProjectInfo[] {
    const projects: ProjectInfo[] = [];

    try {
      
      // 方法1: 年度ベースの抽出
      const yearBasedProjects = this.extractYearBasedProjects(rawData);
      projects.push(...yearBasedProjects);
      
      // 方法2: 技術スタックベースの抽出
      const techBasedProjects = this.extractTechBasedProjects(rawData);
      projects.push(...techBasedProjects);
      
      // 方法3: プロジェクト名ベースの抽出
      const projectNameBasedProjects = this.extractProjectNameBasedProjects(rawData);
      projects.push(...projectNameBasedProjects);
      
      // 方法4: データ全体スキャンによる案件抽出
      const generalScanProjects = this.extractProjectsByGeneralScan(rawData);
      projects.push(...generalScanProjects);
      
      // 方法5: フォールバック - 全ての方法で案件が見つからない場合のみ
      if (projects.length === 0) {
        const fallbackProjects = this.createFallbackProjects(rawData);
        projects.push(...fallbackProjects);
      }
      
      
      // 重複除去とソート
      const uniqueProjects = this.deduplicateProjects(projects);
      
      return uniqueProjects.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

    } catch (error) {
      console.error('案件抽出エラー:', error);
      return [];
    }
  }
  
  /**
   * 年度ベースの案件抽出
   */
  private static extractYearBasedProjects(rawData: any[][]): ProjectInfo[] {
    const projects: ProjectInfo[] = [];
    
    for (let i = 0; i < rawData.length; i++) {
      for (let j = 0; j < rawData[i].length; j++) {
        const cell = String(rawData[i][j] || '');
        
        // 年度パターンを検索 (2020-2030まで拡張)
        const yearMatch = cell.match(/(20[2-3][0-9])/);
        if (yearMatch) {
          const year = parseInt(yearMatch[1]);
          
          const projectInfo = this.extractProjectInfoFromContext(rawData, i, j, year);
          if (projectInfo) {
            projects.push(projectInfo);
          }
        }
      }
    }
    
    return projects;
  }
  
  /**
   * 技術スタックベースの案件抽出
   */
  private static extractTechBasedProjects(rawData: any[][]): ProjectInfo[] {
    const projects: ProjectInfo[] = [];
    
    for (let i = 0; i < rawData.length; i++) {
      const rowTechnologies: string[] = [];
      let hasDate = false;
      let projectName = '';
      
      // 行全体をスキャンして技術と日付を探す
      for (let j = 0; j < rawData[i].length; j++) {
        const cell = String(rawData[i][j] || '');
        
        if (this.isTechnology(cell)) {
          rowTechnologies.push(cell);
        }
        
        if (this.containsDate(cell)) {
          hasDate = true;
        }
        
        if (this.isProjectName(cell)) {
          projectName = cell;
        }
      }
      
      // 技術と日付がある行を案件として認識
      if (rowTechnologies.length > 0 && hasDate) {
        const project: ProjectInfo = {
          startDate: new Date(2023, 0, 1), // デフォルト日付
          endDate: new Date(2023, 11, 31),
          technologies: Array.from(new Set(rowTechnologies)),
          projectName: projectName || `技術プロジェクト${i + 1}`,
          description: `技術: ${rowTechnologies.join(', ')}`
        };
        
        projects.push(project);
      }
    }
    
    return projects;
  }
  
  /**
   * プロジェクト名ベースの案件抽出
   */
  private static extractProjectNameBasedProjects(rawData: any[][]): ProjectInfo[] {
    const projects: ProjectInfo[] = [];
    
    for (let i = 0; i < rawData.length; i++) {
      for (let j = 0; j < rawData[i].length; j++) {
        const cell = String(rawData[i][j] || '');
        
        if (this.isProjectName(cell)) {
          
          // 周辺の技術と日付を探す
          const contextTechs = this.findNearbyTechnologies(rawData, i, j);
          const contextDates = this.findNearbyDates(rawData, i, j);
          
          if (contextTechs.length > 0) {
            const project: ProjectInfo = {
              startDate: contextDates.length > 0 ? contextDates[0] : new Date(2023, 0, 1),
              endDate: contextDates.length > 1 ? contextDates[1] : new Date(2023, 11, 31),
              technologies: contextTechs,
              projectName: cell,
              description: `プロジェクト: ${cell}`
            };
            
            projects.push(project);
          }
        }
      }
    }
    
    return projects;
  }

  /**
   * 指定位置の周辺から案件情報を抽出（改善版）
   */
  private static extractProjectInfoFromContext(
    rawData: any[][], 
    startRow: number, 
    startCol: number, 
    year: number
  ): ProjectInfo | null {
    try {
      let projectName = '';
      let technologies: string[] = [];
      let startDate: Date | null = null;
      let endDate: Date | null = null;

      // 拡張された探索範囲
      const searchRange = {
        rowStart: Math.max(0, startRow - 8),
        rowEnd: Math.min(rawData.length, startRow + 8),
        colStart: Math.max(0, startCol - 10),
        colEnd: Math.min(rawData[0]?.length || 0, startCol + 20)
      };


      for (let r = searchRange.rowStart; r < searchRange.rowEnd; r++) {
        for (let c = searchRange.colStart; c < searchRange.colEnd; c++) {
          const cellValue = String(rawData[r]?.[c] || '');
          
          if (cellValue.trim() === '') continue;
          
          // 案件名の検出
          if (this.isProjectName(cellValue)) {
            projectName = cellValue;
          }

          // 技術名の検出
          if (this.isTechnology(cellValue)) {
            technologies.push(cellValue);
          }

          // 期間情報の検出
          const dateInfo = this.extractDateFromCell(cellValue, year);
          if (dateInfo) {
            if (!startDate || dateInfo < startDate) {
              endDate = startDate;
              startDate = dateInfo;
            } else if (!endDate || dateInfo > endDate) {
              endDate = dateInfo;
            }
          }
        }
      }

      // より柔軟な条件で案件として認識
      if (technologies.length > 0 || projectName) {
        // 日付がない場合はデフォルトを使用
        if (!startDate) {
          startDate = new Date(year, 0, 1);
        }
        if (!endDate) {
          endDate = new Date(year, 11, 31);
        }
        
        // 技術がない場合は汎用的な技術を追加
        if (technologies.length === 0) {
          technologies = ['ウェブ開発', 'システム開発'];
        }

        const project = {
          startDate,
          endDate,
          technologies: Array.from(new Set(technologies)),
          projectName: projectName || `${year}年プロジェクト`,
          description: `${year}年の案件: ${technologies.join(', ')}`
        };
        
        return project;
      }

      return null;
    } catch (error) {
      console.error('案件情報抽出エラー:', error);
      return null;
    }
  }

  /**
   * セルが案件名かどうか判定（改善版）
   */
  private static isProjectName(cellValue: string): boolean {
    const cleanValue = cellValue.trim();
    if (cleanValue.length < 3) return false; // 最低3文字以上
    
    const projectKeywords = [
      // プロジェクト関連
      '開発', 'システム', 'サイト', 'プラットフォーム', 'リニューアル', 
      '制作', '保守', '運用', 'アプリ', 'サービス', 'フロントエンド',
      '管理システム', 'Webサイト', '公式サイト', 'バックエンド',
      'ホームページ', 'ポータル', 'ダッシュボード', 'アプリケーション',
      'ウェブ', 'モバイル', 'EC', '通販', '予約', '決済', 'チャット',
      'マッチング', 'ソーシャル', 'メディア', 'ニュース', 'ブログ',
      // 英語キーワード
      'system', 'site', 'platform', 'application', 'app', 'service', 'project',
      'web', 'mobile', 'frontend', 'backend', 'api', 'portal', 'dashboard',
      'management', 'admin', 'cms', 'blog', 'shop', 'store', 'marketplace'
    ];
    
    // 技術名ではないことを確認
    if (this.isTechnology(cleanValue)) {
      return false;
    }
    
    // キーワードマッチング
    const hasKeyword = projectKeywords.some(keyword => 
      cleanValue.toLowerCase().includes(keyword.toLowerCase())
    );
    
    // キーワードがあるか、ある程度の長さがある文字列
    return hasKeyword || (cleanValue.length >= 5 && cleanValue.length <= 50);
  }

  /**
   * セルが技術名かどうか判定（厳格化版）
   */
  private static isTechnology(cellValue: string): boolean {
    const cleanValue = cellValue.trim();
    if (cleanValue.length < 2) return false;
    
    // 否定文脈をチェック（案件計算でも適用）
    if (this.hasNegativeContext(cellValue)) {
      return false;
    }
    
    // 明確でない文脈をチェック
    if (this.hasVagueContext(cellValue)) {
      return false;
    }
    
    const technologies = [
      // プログラミング言語（厳選）
      'JavaScript', 'TypeScript', 'Python', 'Java', 'PHP', 'C#', 'Go', 'Swift', 'Kotlin', 'Ruby',
      
      // フロントエンド（主要技術のみ）
      'React', 'Vue.js', 'Vue', 'Angular', 'Next.js', 'Nuxt.js', 'HTML', 'CSS', 'SCSS', 'Sass',
      'jQuery', 'Webpack', 'Vite',
      
      // バックエンド・フレームワーク（主要技術のみ）
      'Node.js', 'Express', 'Django', 'Flask', 'Spring Boot', 'Spring', 'Laravel', 'FastAPI',
      
      // データベース
      'MySQL', 'PostgreSQL', 'MongoDB', 'Redis', 'Supabase', 'Firebase',
      
      // クラウド・インフラ
      'AWS', 'Azure', 'GCP', 'Vercel', 'Docker', 'Kubernetes',
      
      // ツール・その他（開発に直接関わるもののみ）
      'Git', 'GitHub', 'Linux'
    ];

    // 厳格なマッチング（完全一致優先）
    const cellLower = cleanValue.toLowerCase();
    
    return technologies.some(tech => {
      const techLower = tech.toLowerCase();
      
      // 完全一致
      if (cellLower === techLower) return true;
      
      // 厳密な部分一致（前後に区切り文字がある場合のみ）
      const wordBoundaryPattern = new RegExp(`\\b${techLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
      if (wordBoundaryPattern.test(cellValue)) {
        return true;
      }
      
      return false;
    });
  }

  /**
   * 否定的な文脈を検出
   */
  private static hasNegativeContext(text: string): boolean {
    const negativePatterns = [
      // 否定表現
      /(?:使用していない|経験がない|わからない|知らない|できない|未経験)/i,
      /(?:やっていない|したことがない|触ったことがない)/i,
      /(?:学習予定|今後学習|将来的に学習|勉強予定)/i,

      // 他人の経験
      /(?:チーム|同僚|他の人|メンバー).*?(?:が使用|が担当|が開発)/i,
      /(?:他の人が|チームが|同僚が)/i,

      // 回避・除外
      /(?:避けて|使わずに|除いて|以外で)/i,
      /(?:ではなく|ではない)/i,

      // 将来の希望
      /(?:挑戦したい|学びたい|やってみたい|興味がある)/i,
    ];

    return negativePatterns.some(pattern => pattern.test(text));
  }

  /**
   * 曖昧な文脈を検出（技術名が単体の場合は除外）
   */
  private static hasVagueContext(text: string): boolean {
    const cleanValue = text.trim();

    // 技術名のみの場合は曖昧ではない（例: "Firebase", "データベース"など）
    if (cleanValue.length < 20) {
      return false;
    }

    const vaguePatterns = [
      /(?:など|等|その他)/i,
      /(?:系|関連|ベース)(?!base)/i, // "Firebase"などを除外
      /(?:について|に関して|について学習)/i,
      /(?:の知識|の理解|の概念)/i,
    ];

    return vaguePatterns.some(pattern => pattern.test(cleanValue));
  }
  
  /**
   * セルに日付情報が含まれているかチェック
   */
  private static containsDate(cellValue: string): boolean {
    const cleanValue = cellValue.trim();
    if (cleanValue.length < 4) return false;
    
    // 年のパターン
    if (/(20[0-3][0-9])/.test(cleanValue)) return true;
    
    // 月のパターン
    if (/([0-9]{1,2})月/.test(cleanValue)) return true;
    
    // 日付フォーマット
    if (/\d{4}[-/]\d{1,2}[-/]\d{1,2}/.test(cleanValue)) return true;
    
    return false;
  }
  
  /**
   * データ全体を総合的にスキャンして案件を抽出（最も包括的な方法）
   * @param rawData - スキャン対象のデータ
   * @param warnings - 警告メッセージを追加する配列（オプション）
   */
  private static extractProjectsByGeneralScan(rawData: any[][], warnings?: string[]): ProjectInfo[] {
    const projects: ProjectInfo[] = [];


    try {
      // エクセル全体を行ごとにスキャンして案件の可能性を探る
      for (let rowIndex = 0; rowIndex < rawData.length; rowIndex++) {
        const row = rawData[rowIndex];
        if (!row || row.length === 0) continue;

        const rowData = this.analyzeRowForProject(row, rowIndex, rawData);
        if (rowData.isProjectRow) {

          // この行の前後数行も合わせて詳細分析
          const contextualProject = this.extractProjectFromRowContext(rawData, rowIndex, rowData);
          if (contextualProject) {
            projects.push(contextualProject);
          }
        }
      }

      // 複数列にまたがる案件情報の抽出
      const multiColumnProjects = this.extractMultiColumnProjects(rawData);
      projects.push(...multiColumnProjects);

      // テーブル形式データの抽出
      const tableProjects = this.extractTableFormatProjects(rawData);
      projects.push(...tableProjects);

    } catch (error) {
      const errorMessage = `プロジェクト情報のスキャン中にエラーが発生しました: ${(error as Error).message}`;
      console.error('全体スキャンエラー:', error);
      if (warnings) {
        warnings.push(errorMessage);
      }
    }

    return projects;
  }
  
  /**
   * 行を分析して案件情報の可能性を判定
   */
  private static analyzeRowForProject(row: any[], rowIndex: number, allData: any[][]): {
    isProjectRow: boolean;
    technologies: string[];
    dates: Date[];
    projectNames: string[];
    confidence: number;
  } {
    const technologies: string[] = [];
    const dates: Date[] = [];
    const projectNames: string[] = [];
    let confidence = 0;
    
    for (let colIndex = 0; colIndex < row.length; colIndex++) {
      const cell = String(row[colIndex] || '').trim();
      if (!cell) continue;
      
      // 技術検出
      if (this.isTechnology(cell)) {
        technologies.push(cell);
        confidence += 0.3;
      }
      
      // 日付検出
      const extractedDate = this.extractDateFromCell(cell, 2023);
      if (extractedDate) {
        dates.push(extractedDate);
        confidence += 0.4;
      }
      
      // 案件名検出
      if (this.isProjectName(cell)) {
        projectNames.push(cell);
        confidence += 0.5;
      }
      
      // 期間を示唆するキーワード
      if (this.containsDate(cell) || /期間|から|まで|〜/.test(cell)) {
        confidence += 0.2;
      }
    }
    
    const isProjectRow = (technologies.length > 0 && dates.length > 0) || 
                        (projectNames.length > 0 && (technologies.length > 0 || dates.length > 0)) ||
                        confidence >= 0.8;
    
    return {
      isProjectRow,
      technologies: Array.from(new Set(technologies)),
      dates: dates.sort((a, b) => a.getTime() - b.getTime()),
      projectNames,
      confidence
    };
  }
  
  /**
   * 行の前後コンテキストも含めて案件情報を抽出
   */
  private static extractProjectFromRowContext(rawData: any[][], centerRow: number, rowData: any): ProjectInfo | null {
    try {
      let technologies = [...rowData.technologies];
      let dates = [...rowData.dates];
      let projectName = rowData.projectNames[0] || '';
      
      // 前後3行も確認
      const contextRange = 3;
      for (let offset = -contextRange; offset <= contextRange; offset++) {
        const checkRow = centerRow + offset;
        if (checkRow < 0 || checkRow >= rawData.length) continue;
        if (checkRow === centerRow) continue; // 中心行はすでに処理済み
        
        const contextRowData = this.analyzeRowForProject(rawData[checkRow], checkRow, rawData);
        
        // 追加の技術があれば統合
        technologies.push(...contextRowData.technologies);
        
        // 追加の日付があれば統合
        dates.push(...contextRowData.dates);
        
        // 案件名がまだない場合は採用
        if (!projectName && contextRowData.projectNames.length > 0) {
          projectName = contextRowData.projectNames[0];
        }
      }
      
      // 重複除去
      technologies = Array.from(new Set(technologies));
      dates = dates.sort((a, b) => a.getTime() - b.getTime());
      
      if (technologies.length === 0) {
        return null;
      }
      
      // 日付の決定
      let startDate: Date, endDate: Date;
      if (dates.length >= 2) {
        startDate = dates[0];
        endDate = dates[dates.length - 1];
      } else if (dates.length === 1) {
        startDate = dates[0];
        endDate = new Date(dates[0].getFullYear(), 11, 31); // 同年の年末
      } else {
        // 日付がない場合はデフォルト
        const currentYear = new Date().getFullYear();
        startDate = new Date(currentYear - 1, 0, 1);
        endDate = new Date(currentYear - 1, 11, 31);
      }
      
      const project: ProjectInfo = {
        startDate,
        endDate,
        technologies,
        projectName: projectName || `プロジェクト(行${centerRow + 1})`,
        description: `技術: ${technologies.join(', ')}`
      };
      
      return project;
      
    } catch (error) {
      console.error('行コンテキスト抽出エラー:', error);
      return null;
    }
  }
  
  /**
   * 複数列にまたがる案件情報を抽出
   */
  private static extractMultiColumnProjects(rawData: any[][]): ProjectInfo[] {
    const projects: ProjectInfo[] = [];
    
    try {
      // 各行で技術が複数列に分散している可能性をチェック
      for (let row = 0; row < rawData.length; row++) {
        const rowTechnologies: string[] = [];
        const rowDates: Date[] = [];
        let projectName = '';
        
        // 行全体をスキャン
        for (let col = 0; col < rawData[row]?.length || 0; col++) {
          const cell = String(rawData[row][col] || '').trim();
          
          if (this.isTechnology(cell)) {
            rowTechnologies.push(cell);
          }
          
          const date = this.extractDateFromCell(cell, 2023);
          if (date) {
            rowDates.push(date);
          }
          
          if (this.isProjectName(cell) && !projectName) {
            projectName = cell;
          }
        }
        
        // この行に十分な案件情報があるかチェック
        if (rowTechnologies.length >= 2 || (rowTechnologies.length >= 1 && rowDates.length >= 1)) {
          const startDate = rowDates.length > 0 ? rowDates[0] : new Date(2023, 0, 1);
          const endDate = rowDates.length > 1 ? rowDates[rowDates.length - 1] : new Date(2023, 11, 31);
          
          const project: ProjectInfo = {
            startDate,
            endDate,
            technologies: Array.from(new Set(rowTechnologies)),
            projectName: projectName || `マルチ列プロジェクト(行${row + 1})`,
            description: `複数列技術: ${rowTechnologies.join(', ')}`
          };
          
          projects.push(project);
        }
      }
      
    } catch (error) {
      console.error('複数列抽出エラー:', error);
    }
    
    return projects;
  }
  
  /**
   * テーブル形式のデータから案件を抽出
   */
  private static extractTableFormatProjects(rawData: any[][]): ProjectInfo[] {
    const projects: ProjectInfo[] = [];
    
    try {
      // ヘッダー行を探す
      let headerRow = -1;
      const possibleHeaders = ['案件', 'プロジェクト', '技術', 'スキル', '期間', '開始', '終了', 'project', 'skill', 'technology'];
      
      for (let row = 0; row < Math.min(5, rawData.length); row++) {
        const rowText = rawData[row]?.join(' ').toLowerCase() || '';
        const hasHeaders = possibleHeaders.some(header => rowText.includes(header));
        
        if (hasHeaders) {
          headerRow = row;
          break;
        }
      }
      
      if (headerRow >= 0) {
        // ヘッダー以降の行をデータ行として処理
        for (let row = headerRow + 1; row < rawData.length; row++) {
          const rowData = rawData[row];
          if (!rowData || rowData.length === 0) continue;
          
          const technologies: string[] = [];
          const dates: Date[] = [];
          let projectName = '';
          
          for (const cell of rowData) {
            const cellStr = String(cell || '').trim();
            
            if (this.isTechnology(cellStr)) {
              technologies.push(cellStr);
            }
            
            const date = this.extractDateFromCell(cellStr, 2023);
            if (date) {
              dates.push(date);
            }
            
            if (this.isProjectName(cellStr) && !projectName) {
              projectName = cellStr;
            }
          }
          
          if (technologies.length > 0) {
            const startDate = dates.length > 0 ? dates[0] : new Date(2023, 0, 1);
            const endDate = dates.length > 1 ? dates[dates.length - 1] : new Date(2023, 11, 31);
            
            const project: ProjectInfo = {
              startDate,
              endDate,
              technologies: Array.from(new Set(technologies)),
              projectName: projectName || `テーブル案件(行${row + 1})`,
              description: `テーブル形式: ${technologies.join(', ')}`
            };
            
            projects.push(project);
          }
        }
      }
      
    } catch (error) {
      console.error('テーブル形式抽出エラー:', error);
    }
    
    return projects;
  }

  /**
   * 指定位置の近くの技術を探す
   */
  private static findNearbyTechnologies(rawData: any[][], centerRow: number, centerCol: number): string[] {
    const technologies: string[] = [];
    const searchRange = 3; // 探索範囲
    
    for (let i = Math.max(0, centerRow - searchRange); i <= Math.min(rawData.length - 1, centerRow + searchRange); i++) {
      for (let j = Math.max(0, centerCol - searchRange); j <= Math.min(rawData[i]?.length - 1 || 0, centerCol + searchRange); j++) {
        const cell = String(rawData[i]?.[j] || '');
        if (this.isTechnology(cell)) {
          technologies.push(cell);
        }
      }
    }
    
    return Array.from(new Set(technologies));
  }
  
  /**
   * 指定位置の近くの日付を探す
   */
  private static findNearbyDates(rawData: any[][], centerRow: number, centerCol: number): Date[] {
    const dates: Date[] = [];
    const searchRange = 3;
    
    for (let i = Math.max(0, centerRow - searchRange); i <= Math.min(rawData.length - 1, centerRow + searchRange); i++) {
      for (let j = Math.max(0, centerCol - searchRange); j <= Math.min(rawData[i]?.length - 1 || 0, centerCol + searchRange); j++) {
        const cell = String(rawData[i]?.[j] || '');
        const extractedDate = this.extractDateFromCell(cell, 2023);
        if (extractedDate) {
          dates.push(extractedDate);
        }
      }
    }
    
    return dates.sort((a, b) => a.getTime() - b.getTime());
  }
  
  /**
   * フォールバック: データから技術を見つけて強制的に案件を作成
   */
  private static createFallbackProjects(rawData: any[][]): ProjectInfo[] {
    const projects: ProjectInfo[] = [];
    const allTechnologies: string[] = [];
    
    
    // 全データから技術を収集
    for (let i = 0; i < rawData.length; i++) {
      for (let j = 0; j < rawData[i].length; j++) {
        const cell = String(rawData[i][j] || '');
        if (this.isTechnology(cell)) {
          allTechnologies.push(cell);
        }
      }
    }
    
    // 技術が見つかった場合、実際のデータに基づいた案件を作成
    if (allTechnologies.length > 0) {
      const uniqueTechs = Array.from(new Set(allTechnologies));
      
      // 実際に見つかった年度を基準にする（デフォルトは2023年のみ）
      const currentYear = new Date().getFullYear();
      const project: ProjectInfo = {
        startDate: new Date(currentYear - 1, 0, 1), // 前年
        endDate: new Date(currentYear - 1, 11, 31),
        technologies: uniqueTechs.slice(0, Math.min(3, uniqueTechs.length)), // 最大3つまでに制限
        projectName: `${currentYear - 1}年開発プロジェクト`,
        description: `検出された技術: ${uniqueTechs.slice(0, 3).join(', ')}`
      };
      
      projects.push(project);
    }
    
    // それでも何も見つからない場合、汎用的な案件を作成
    if (projects.length === 0) {
      const genericProject: ProjectInfo = {
        startDate: new Date(2023, 0, 1),
        endDate: new Date(2023, 11, 31),
        technologies: ['JavaScript', 'HTML', 'CSS'],
        projectName: 'ウェブ開発プロジェクト',
        description: '一般的なウェブ開発案件'
      };
      
      projects.push(genericProject);
    }
    
    return projects;
  }

  /**
   * セルから日付情報を抽出
   * @param cellValue - セルの値
   * @param baseYear - 基準年（月のみの場合に使用）
   * @param warnings - 警告メッセージを追加する配列（オプション）
   */
  private static extractDateFromCell(cellValue: string, baseYear: number, warnings?: string[]): Date | null {
    try {
      const cleanValue = cellValue.trim();
      if (!cleanValue) return null;

      // Excelの日付形式を処理
      if (cleanValue.includes('00:00:00')) {
        const dateObj = new Date(cleanValue);
        if (!isNaN(dateObj.getTime())) {
          return dateObj;
        }
      }

      // ISO形式の日付 (例: "2025-06-01", "2025-04-01")
      const isoMatch = cleanValue.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
      if (isoMatch) {
        const year = parseInt(isoMatch[1]);
        const month = parseInt(isoMatch[2]);
        const day = parseInt(isoMatch[3]);
        const date = new Date(year, month - 1, day);
        return date;
      }

      // 年月形式の検出 (例: "2025年6月", "2025-06")
      const yearMonthMatch = cleanValue.match(/(\d{4})[年\-/](\d{1,2})/);
      if (yearMonthMatch) {
        const year = parseInt(yearMonthMatch[1]);
        const month = parseInt(yearMonthMatch[2]);
        const date = new Date(year, month - 1, 1);
        return date;
      }

      // 月のみの情報 (例: "6月", "06")
      const monthMatch = cleanValue.match(/(\d{1,2})月/);
      if (monthMatch) {
        const month = parseInt(monthMatch[1]);
        const date = new Date(baseYear, month - 1, 1);
        return date;
      }

      // 年のみの情報 (例: "2025")
      if (/^\d{4}$/.test(cleanValue)) {
        const year = parseInt(cleanValue);
        if (year >= 2020 && year <= 2030) {
          const date = new Date(year, 0, 1);
          return date;
        }
      }

      return null;
    } catch (error) {
      const warningMsg = `日付解析エラー: "${cellValue}" - ${(error as Error).message}`;
      console.warn(warningMsg);
      if (warnings) {
        warnings.push(warningMsg);
      }
      return null;
    }
  }

  /**
   * 案件の重複を除去
   */
  private static deduplicateProjects(projects: ProjectInfo[]): ProjectInfo[] {
    const uniqueProjects: ProjectInfo[] = [];
    const seen = new Set<string>();

    for (const project of projects) {
      // プロジェクトの一意キーを生成
      const key = `${project.startDate.getTime()}-${project.technologies.sort().join(',')}-${project.projectName}`;
      
      if (!seen.has(key)) {
        seen.add(key);
        uniqueProjects.push(project);
      }
    }

    return uniqueProjects;
  }

  /**
   * 技術経験年数を計算
   */
  private static calculateTechnologyExperience(projects: ProjectInfo[]): ExperienceCalculationResult {
    const technologies: { [technology: string]: TechnologyExperience } = {};
    const warnings: string[] = [];
    const insights: string[] = [];
    const today = new Date();

    // 各プロジェクトを処理
    for (const project of projects) {
      const startDate = project.startDate;
      const endDate = project.endDate || today;
      
      // プロジェクト期間（月数）を計算
      const durationMonths = this.calculateMonthsDifference(startDate, endDate);
      
      if (durationMonths <= 0) {
        warnings.push(`無効な期間のプロジェクト: ${project.projectName}`);
        continue;
      }

      // 各技術について経験を記録
      for (const tech of project.technologies) {
        if (!technologies[tech]) {
          technologies[tech] = {
            technology: tech,
            totalYears: 0,
            projects: [],
            firstUsed: startDate,
            lastUsed: endDate,
            confidence: 1.0
          };
        }

        const techExp = technologies[tech];
        
        // 期間情報を追加
        techExp.projects.push({
          projectName: project.projectName,
          startDate,
          endDate,
          durationMonths
        });

        // 最初・最後の使用日を更新
        if (startDate < techExp.firstUsed) {
          techExp.firstUsed = startDate;
        }
        if (endDate > techExp.lastUsed) {
          techExp.lastUsed = endDate;
        }
      }
    }

    // 各技術の総経験年数を計算（最小経験期間の閾値適用）
    const MIN_EXPERIENCE_MONTHS = 1; // 最低1ヶ月の経験が必要
    const validTechnologies: { [technology: string]: TechnologyExperience } = {};
    
    for (const tech of Object.keys(technologies)) {
      const techExp = technologies[tech];
      
      // 重複期間を考慮した経験年数計算
      const totalMonths = this.calculateNonOverlappingExperience(techExp.projects);
      
      // 最小経験期間未満は除外
      if (totalMonths < MIN_EXPERIENCE_MONTHS) {
        warnings.push(`${tech}: 経験期間が短すぎるため除外 (${Math.round(totalMonths)}ヶ月)`);
        continue;
      }

      // より精密な年数計算（小数点第2位まで計算してから第1位で四捨五入）
      techExp.totalYears = Math.round((totalMonths / 12) * 100) / 100; // 小数点2桁まで精密に計算

      // 表示用に小数点1桁に四捨五入
      const displayYears = Math.round(techExp.totalYears * 10) / 10;

      // 信頼度を計算 (案件数とデータの整合性に基づく)
      techExp.confidence = this.calculateConfidence(techExp);

      // 信頼度が低い技術も除外
      if (techExp.confidence < 0.7) {
        warnings.push(`${tech}: 信頼度が低いため除外`);
        continue;
      }

      // 有効な技術として採用
      validTechnologies[tech] = techExp;

      // インサイトを生成（表示用の年数を使用）
      if (displayYears >= 3) {
        insights.push(`${tech}: ${displayYears}年の豊富な経験`);
      } else if (displayYears >= 1) {
        insights.push(`${tech}: ${displayYears}年の実務経験`);
      } else {
        insights.push(`${tech}: ${displayYears}年の経験`);
      }
    }

    // 全体の経験年数を計算
    const allStartDates = projects.map(p => p.startDate);
    const earliestStart = allStartDates.length > 0 ? new Date(Math.min(...allStartDates.map(d => d.getTime()))) : today;
    const totalExperienceYears = Math.round(this.calculateYearsDifference(earliestStart, today) * 10) / 10;

    return {
      technologies: validTechnologies,
      totalExperienceYears,
      analysisDate: new Date(),
      warnings,
      insights
    };
  }

  /**
   * 重複しない経験期間を計算（改善版）
   */
  private static calculateNonOverlappingExperience(
    projects: { startDate: Date; endDate?: Date; durationMonths: number }[]
  ): number {
    if (projects.length === 0) return 0;

    // 期間をソート
    const sortedProjects = projects
      .filter(p => p.endDate)
      .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

    if (sortedProjects.length === 0) return 0;

    
    // 各プロジェクトの期間を結合して重複を除去
    const periods: { start: Date; end: Date }[] = [];
    
    for (const project of sortedProjects) {
      periods.push({ start: project.startDate, end: project.endDate! });
    }
    
    // 期間をマージして重複を除去
    const mergedPeriods = this.mergePeriods(periods);
    
    // マージされた期間の総月数を計算
    let totalMonths = 0;
    for (const period of mergedPeriods) {
      const months = this.calculateMonthsDifference(period.start, period.end);
      totalMonths += months;
    }
    
    return totalMonths;
  }
  
  /**
   * 期間をマージして重複を除去
   */
  private static mergePeriods(periods: { start: Date; end: Date }[]): { start: Date; end: Date }[] {
    if (periods.length === 0) return [];
    
    // 開始日でソート
    const sorted = periods.sort((a, b) => a.start.getTime() - b.start.getTime());
    const merged: { start: Date; end: Date }[] = [];
    
    let current = { start: sorted[0].start, end: sorted[0].end };
    
    for (let i = 1; i < sorted.length; i++) {
      const next = sorted[i];
      
      // 現在の期間と重複または続いているかチェック
      if (next.start <= current.end) {
        // 重複しているのでマージ
        current.end = new Date(Math.max(current.end.getTime(), next.end.getTime()));
      } else {
        // 重複していないので現在の期間を確定し、新しい期間を開始
        merged.push(current);
        current = { start: next.start, end: next.end };
      }
    }
    
    // 最後の期間を追加
    merged.push(current);
    
    return merged;
  }

  /**
   * 信頼度を計算
   */
  private static calculateConfidence(techExp: TechnologyExperience): number {
    let confidence = 1.0;

    // 案件数が少ない場合は信頼度を下げる
    if (techExp.projects.length === 1) {
      confidence *= 0.8;
    } else if (techExp.projects.length >= 3) {
      confidence *= 1.1; // 最大1.0に制限される
    }

    // 期間が非現実的に長い/短い場合
    if (techExp.totalYears > 20) {
      confidence *= 0.7;
    } else if (techExp.totalYears < 0.1) {
      confidence *= 0.8;
    }

    return Math.min(confidence, 1.0);
  }

  /**
   * 月数の差を計算（日数を考慮した精密版）
   *
   * アルゴリズム:
   * 1. 年と月の差を計算
   * 2. 日数による調整を適用（30日基準）
   * 3. 開始日と終了日が異なり、計算結果が0以下の場合は最小0.1ヶ月を保証
   *
   * @param startDate - 開始日
   * @param endDate - 終了日
   * @returns 月数（小数点含む、開始日===終了日の場合は0）
   */
  private static calculateMonthsDifference(startDate: Date, endDate: Date): number {
    // より正確な計算方法
    const startYear = startDate.getFullYear();
    const startMonth = startDate.getMonth();
    const startDay = startDate.getDate();

    const endYear = endDate.getFullYear();
    const endMonth = endDate.getMonth();
    const endDay = endDate.getDate();

    // 年と月の差を計算
    let totalMonths = (endYear - startYear) * 12 + (endMonth - startMonth);

    // 日数による調整
    if (endDay >= startDay) {
      // 終了日が開始日以降の場合、月をそのまま加算
      totalMonths += (endDay - startDay) / 30;
    } else {
      // 終了日が開始日より前の場合、1ヶ月減らして調整
      totalMonths -= 1;
      // 前月の残り日数 + 今月の日数
      const daysInPrevMonth = new Date(endYear, endMonth, 0).getDate();
      totalMonths += (daysInPrevMonth - startDay + endDay) / 30;
    }

    // 最低でも開始日と終了日が異なれば何らかの期間があると見なす
    if (totalMonths <= 0 && startDate.getTime() !== endDate.getTime()) {
      totalMonths = 0.1; // 最小値として0.1ヶ月
    }


    return Math.max(0, totalMonths);
  }

  /**
   * 年数の差を計算
   */
  private static calculateYearsDifference(startDate: Date, endDate: Date): number {
    return (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
  }

  /**
   * 技術経験をスキル形式に変換
   */
  static convertToSkillFormat(
    technologies: { [technology: string]: TechnologyExperience }
  ): { skill_name: string; skill_level: 'beginner' | 'intermediate' | 'advanced' | 'expert'; years_experience: number }[] {
    return Object.values(technologies).map(tech => ({
      skill_name: tech.technology,
      skill_level: this.determineSkillLevel(tech.totalYears),
      years_experience: Math.round(tech.totalYears)
    }));
  }

  /**
   * 経験年数からスキルレベルを判定
   */
  private static determineSkillLevel(years: number): 'beginner' | 'intermediate' | 'advanced' | 'expert' {
    if (years >= 5) return 'expert';
    if (years >= 3) return 'advanced';
    if (years >= 1) return 'intermediate';
    return 'beginner';
  }
}

export default ProjectExperienceCalculator;